import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { lastWeek } from '../planner/lastWeek';

type SidePanelProps = {
  title: string;
  description?: string;
  children: ReactNode;
};

/** The one panel every settings screen opens in: right side, over the sheet; closing returns to the last week. */
export function SidePanel({ title, description, children }: SidePanelProps) {
  const navigate = useNavigate();
  const close = () => {
    const week = lastWeek();
    void navigate(`/week/${week.year}/${week.week}`);
  };
  return (
    <Sheet open onOpenChange={(open) => !open && close()}>
      <SheetContent
        side="right"
        showCloseButton={false}
        // No blur on the overlay: the sheet behind the panel stays sharp, it moves along with every change.
        overlayClassName="bg-black/10 supports-backdrop-filter:backdrop-blur-none"
        className="w-full gap-4 overflow-y-auto p-4 data-[side=right]:w-full data-[side=right]:sm:max-w-[420px]"
      >
        <SheetHeader className="p-0 pr-12 text-left">
          <SheetTitle className="text-2xl font-extrabold" style={{ fontFamily: 'var(--font-heading)' }}>
            {title}
          </SheetTitle>
          <SheetDescription className={description ? undefined : 'sr-only'}>{description ?? title}</SheetDescription>
        </SheetHeader>
        <SheetClose asChild>
          <Button variant="ghost" size="icon" aria-label="Sluiten" className="absolute top-2 right-2 min-h-11 min-w-11">
            <X />
          </Button>
        </SheetClose>
        {children}
      </SheetContent>
    </Sheet>
  );
}
